export interface TicketPayStatusResult {
    dappNo: string;
    ticketNo: string;
    payStatus: number;
}

export interface TicketPaymentData {
    dappNo?: string;
    token: string;
    price: string | number;
    ticketNo: string;
    signature: string;
}

export interface TicketPaymentResult {
    dappNo: string;
    ticketNo: string;
    txHash: string;
    payStatus: number;
}

export type TicketPaymentMode = 'once' | 'subscribe';

export interface TicketPaymentFlowOptions<TAmount = unknown> {
    dappNo: string;
    payment: TicketPaymentMode;
    parseAmount: (amount: string) => TAmount;
    createTicket: (dappNo: string) => Promise<TicketPaymentData>;
    subscribeDappNo: (dappNo: string) => Promise<TicketPaymentData>;
    payTicket: (dappNo: string, token: string, amount: TAmount, ticketNo: string, signature: string) => Promise<{ hash: string }>;
    subscribe: (dappNo: string, token: string, amount: TAmount, signature: string) => Promise<{ hash: string }>;
    updatePadding: (dappNo: string, ticketNo: string, txHash: string) => Promise<unknown>;
    getTicketPayStatus: (dappNo: string, ticketNo: string) => Promise<TicketPayStatusResult>;
    wait?: (ms: number) => Promise<unknown>;
    statusIntervalMs?: number;
    maxStatusAttempts?: number;
}

export interface PollTicketPayStatusOptions {
    dappNo: string;
    ticketNo: string;
    getTicketPayStatus: (dappNo: string, ticketNo: string) => Promise<TicketPayStatusResult>;
    wait?: (ms: number) => Promise<unknown>;
    intervalMs?: number;
    maxAttempts?: number;
}

const defaultWait = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

export const pollTicketPayStatus = async (options: PollTicketPayStatusOptions): Promise<TicketPayStatusResult> => {
    const wait = options.wait ?? defaultWait;
    const intervalMs = options.intervalMs ?? 2000;
    const maxAttempts = options.maxAttempts ?? 150;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (attempt > 0) {
            await wait(intervalMs);
        }

        const status = await options.getTicketPayStatus(options.dappNo, options.ticketNo);
        if (status.payStatus === 1) {
            return status;
        }
    }

    throw new Error('TICKET_PAYMENT_CONFIRM_TIMEOUT');
};

export const payDappTicket = async <TAmount = unknown>(options: TicketPaymentFlowOptions<TAmount>): Promise<TicketPaymentResult> => {
    const ticketData = options.payment === 'subscribe' ? await options.subscribeDappNo(options.dappNo) : await options.createTicket(options.dappNo);
    const dappNo = ticketData.dappNo || options.dappNo;
    const amount = options.parseAmount(`${ticketData.price}`);
    const transaction =
        options.payment === 'subscribe'
            ? await options.subscribe(dappNo, ticketData.token, amount, ticketData.signature)
            : await options.payTicket(dappNo, ticketData.token, amount, ticketData.ticketNo, ticketData.signature);

    await options.updatePadding(dappNo, ticketData.ticketNo, transaction.hash);

    const paymentStatus = await pollTicketPayStatus({
        dappNo,
        ticketNo: ticketData.ticketNo,
        getTicketPayStatus: options.getTicketPayStatus,
        wait: options.wait,
        intervalMs: options.statusIntervalMs,
        maxAttempts: options.maxStatusAttempts,
    });

    return {
        dappNo,
        ticketNo: ticketData.ticketNo,
        txHash: transaction.hash,
        payStatus: paymentStatus.payStatus,
    };
};
